/**
 * Progress Bar Component for Poker Reborn
 *
 * A smooth, animated progress bar for loading screens.
 *
 * @see docs/screens/loading/README.md - Loading screen progress bar specs
 */

import { Container, Graphics } from 'pixi.js';

// =============================================================================
// TYPES
// =============================================================================

export interface ProgressBarStyle {
  /** Background color */
  backgroundColor: number;

  /** Fill color */
  fillColor: number;

  /** Secondary fill color for gradient effect */
  fillColorSecondary?: number;

  /** Border color */
  borderColor: number;

  /** Border width */
  borderWidth: number;

  /** Corner radius */
  cornerRadius: number;

  /** Glow effect */
  glow: boolean;

  /** Glow color */
  glowColor: number;
}

export interface ProgressBarOptions {
  /** Bar width */
  width: number;

  /** Bar height */
  height: number;

  /** Initial progress (0-1) */
  progress?: number;

  /** Custom style overrides */
  style?: Partial<ProgressBarStyle>;
}

// =============================================================================
// DEFAULT STYLE
// =============================================================================

const DEFAULT_STYLE: ProgressBarStyle = {
  backgroundColor: 0x1a1a2e,
  fillColor: 0x4aba44,
  fillColorSecondary: 0x2d8a27,
  borderColor: 0x333355,
  borderWidth: 2,
  cornerRadius: 8,
  glow: true,
  glowColor: 0x4aba44,
};

// =============================================================================
// PROGRESS BAR COMPONENT
// =============================================================================

export class ProgressBar extends Container {
  private background: Graphics;
  private fill: Graphics;
  private glowEffect: Graphics;
  private style: ProgressBarStyle;

  private _width: number;
  private _height: number;
  private _progress = 0;
  private _targetProgress = 0;

  private animationSpeed = 0.1;

  constructor(options: ProgressBarOptions) {
    super();

    this._width = options.width;
    this._height = options.height;
    this.style = { ...DEFAULT_STYLE, ...options.style };

    // Create glow effect (behind everything)
    this.glowEffect = new Graphics();
    this.glowEffect.alpha = 0.5;
    this.addChild(this.glowEffect);

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create fill
    this.fill = new Graphics();
    this.addChild(this.fill);

    // Initial render
    this.renderBackground();
    this.setProgress(options.progress ?? 0);
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Get current progress (0-1) */
  get progress(): number {
    return this._progress;
  }

  /** Set progress (0-1), animates smoothly */
  setProgress(value: number, animate = true): void {
    this._targetProgress = Math.max(0, Math.min(1, value));

    if (!animate) {
      this._progress = this._targetProgress;
      this.renderFill();
    }
  }

  /** Set progress immediately without animation */
  setProgressImmediate(value: number): void {
    this.setProgress(value, false);
  }

  /** Update animation (call each frame) */
  update(_deltaTime: number): void {
    if (Math.abs(this._progress - this._targetProgress) > 0.001) {
      this._progress += (this._targetProgress - this._progress) * this.animationSpeed;

      // Snap to target when close enough
      if (Math.abs(this._progress - this._targetProgress) < 0.001) {
        this._progress = this._targetProgress;
      }

      this.renderFill();
    }
  }

  /** Resize the progress bar */
  resize(width: number, height: number): void {
    this._width = width;
    this._height = height;
    this.renderBackground();
    this.renderFill();
  }

  /** Update style */
  setStyle(style: Partial<ProgressBarStyle>): void {
    this.style = { ...this.style, ...style };
    this.renderBackground();
    this.renderFill();
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private renderBackground(): void {
    const { style, _width: w, _height: h } = this;

    this.background.clear();

    // Draw background
    this.background.roundRect(0, 0, w, h, style.cornerRadius);
    this.background.fill(style.backgroundColor);

    // Draw border
    if (style.borderWidth > 0) {
      this.background.roundRect(0, 0, w, h, style.cornerRadius);
      this.background.stroke({ width: style.borderWidth, color: style.borderColor });
    }
  }

  private renderFill(): void {
    const { style, _width: w, _height: h, _progress: p } = this;

    this.fill.clear();
    this.glowEffect.clear();

    if (p <= 0) return;

    const padding = style.borderWidth;
    const fillWidth = Math.max(0, (w - padding * 2) * p);
    const fillHeight = h - padding * 2;

    if (fillWidth <= 0) return;

    // Draw fill
    this.fill.roundRect(
      padding,
      padding,
      fillWidth,
      fillHeight,
      Math.max(0, style.cornerRadius - padding)
    );
    this.fill.fill(style.fillColor);

    // Draw glow effect
    if (style.glow && p > 0) {
      const glowSize = 4;
      this.glowEffect.roundRect(
        padding - glowSize,
        padding - glowSize,
        fillWidth + glowSize * 2,
        fillHeight + glowSize * 2,
        style.cornerRadius
      );
      this.glowEffect.fill(style.glowColor);

      // Pulse the glow
      this.glowEffect.alpha = 0.3 + Math.sin(Date.now() / 300) * 0.1;
    }
  }
}

// =============================================================================
// PRESET STYLES
// =============================================================================

/** Green progress bar (default) */
export const GREEN_PROGRESS_STYLE: Partial<ProgressBarStyle> = {
  fillColor: 0x4aba44,
  fillColorSecondary: 0x2d8a27,
  glowColor: 0x4aba44,
};

/** Blue progress bar */
export const BLUE_PROGRESS_STYLE: Partial<ProgressBarStyle> = {
  fillColor: 0x4a8ab4,
  fillColorSecondary: 0x2d6a94,
  glowColor: 0x4a8ab4,
};

/** Gold progress bar */
export const GOLD_PROGRESS_STYLE: Partial<ProgressBarStyle> = {
  fillColor: 0xffd700,
  fillColorSecondary: 0xb8860b,
  glowColor: 0xffd700,
};

/** Red progress bar (for timers) */
export const RED_PROGRESS_STYLE: Partial<ProgressBarStyle> = {
  fillColor: 0xba4a4a,
  fillColorSecondary: 0x8a2d2d,
  glowColor: 0xba4a4a,
};
